import { useCallback } from 'react'
import type { MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import AssistantHistoryItemActions from 'tripPlanner/assistant/components/AssistantHistoryItemActions'
import RecentItemInlineTitleEdit from 'tripPlanner/assistant/components/RecentItemInlineTitleEdit'
import useBeginRecentItemRename from 'tripPlanner/assistant/components/useBeginRecentItemRename'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import { getRecentChatItemUrl } from 'tripPlanner/pages/chat/recentChatItems'
import type { RecentAiChatItem } from 'tripPlanner/utils/recentAiChatItem'
import { isModifiedClick } from './isModifiedClick'
import TripPlannerNavigationRow from './TripPlannerNavigationRow'

interface Props {
  item: RecentAiChatItem
  current: boolean
  onSelect: (chatId: number) => void
  onRename: (chatId: number, name: string) => Promise<void>
  onDelete: (chatId: number) => void
  onDownloadPdf: (() => void) | undefined
  onDownloadSpreadsheet: (() => void) | undefined
}

function TripPlannerNavigationChatRow({
  item,
  current,
  onSelect,
  onRename,
  onDelete,
  onDownloadPdf,
  onDownloadSpreadsheet,
}: Props) {
  const intl = useIntl()
  const isMobile = useScreenSizeOnly('mobile')
  const fallbackTitle = intl.formatMessage(tripPlannerGlossaryMessages.untitled)
  const {
    isRenaming,
    setIsRenaming,
    titleEditRef,
    beginRename,
    displayTitle,
    handleRenamed,
  } = useBeginRecentItemRename(item.title, fallbackTitle)

  const handleClick = useCallback((event: MouseEvent) => {
    if (isModifiedClick(event)) {
      return
    }

    event.preventDefault()
    onSelect(item.id)
  }, [item.id, onSelect])

  const handleRename = useCallback(async(name: string) => {
    await onRename(item.id, name)
    handleRenamed(name)
  }, [handleRenamed, item.id, onRename])

  const handleDelete = useCallback(() => {
    onDelete(item.id)
  }, [item.id, onDelete])

  const isDesktopRenaming = !isMobile && isRenaming

  return (
    <TripPlannerNavigationRow
      to={isDesktopRenaming ? undefined : getRecentChatItemUrl(item)}
      onClick={isDesktopRenaming ? undefined : handleClick}
      current={current}
      title={
        <RecentItemInlineTitleEdit
          ref={titleEditRef}
          title={displayTitle}
          fallbackTitle={fallbackTitle}
          isEditing={isDesktopRenaming}
          weight={current ? 'bold' : 'normal'}
          onEditingChange={setIsRenaming}
          onRename={handleRename}
        />
      }
      actions={
        <AssistantHistoryItemActions
          title={displayTitle}
          onBeginRename={beginRename}
          onRename={handleRename}
          onDelete={handleDelete}
          onDownloadPdf={onDownloadPdf}
          onDownloadSpreadsheet={onDownloadSpreadsheet}
        />
      }
    />
  )
}

export default TripPlannerNavigationChatRow
